import * as React from 'react';
import {
  createTheme,
  ThemeProvider,
  type Theme,
  type ThemeOptions,
} from '@mui/material/styles';
import { useTheme } from './ThemeContext';

function readCssVariable(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

function buildMuiTheme(isDark: boolean): Theme {
  const options: ThemeOptions = {
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: readCssVariable('--color-primary', isDark ? '#60A5FA' : '#1D4ED8'),
      },
      secondary: {
        main: readCssVariable('--color-accent', isDark ? '#A78BFA' : '#7C3AED'),
      },
      error: {
        main: readCssVariable(
          '--color-sla-breached-solid',
          isDark ? '#F87171' : '#B91C1C',
        ),
      },
      warning: {
        main: readCssVariable(
          '--color-sla-at-risk-solid',
          isDark ? '#FBBF24' : '#B45309',
        ),
      },
      success: {
        main: readCssVariable(
          '--color-sla-healthy-solid',
          isDark ? '#34D399' : '#047857',
        ),
      },
      info: {
        main: readCssVariable(
          '--color-status-new-solid',
          isDark ? '#60A5FA' : '#1D4ED8',
        ),
      },
      background: {
        default: readCssVariable(
          '--color-page-bg',
          isDark ? '#0F172A' : '#F8FAFC',
        ),
        paper: readCssVariable(
          '--color-surface',
          isDark ? '#1E293B' : '#FFFFFF',
        ),
      },
      text: {
        primary: readCssVariable(
          '--color-text-primary',
          isDark ? '#F1F5F9' : '#0F172A',
        ),
        secondary: readCssVariable(
          '--color-text-secondary',
          isDark ? '#CBD5E1' : '#475569',
        ),
        disabled: readCssVariable(
          '--color-text-muted',
          isDark ? '#94A3B8' : '#94A3B8',
        ),
      },
      divider: readCssVariable(
        '--color-border',
        isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.12)',
      ),
    },
    typography: {
      fontFamily: '"Inter", "Segoe UI Variable", "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      htmlFontSize: 16,
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      h1: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.015em' },
      h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 },
      h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
      h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.45 },
      h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
      subtitle1: { fontSize: '0.9375rem', fontWeight: 500 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 500 },
      body1: { fontSize: '0.875rem', lineHeight: 1.5 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
      button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' },
      caption: { fontSize: '0.75rem', lineHeight: 1.4 },
      overline: { fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.08em' },
    },
    shape: {
      borderRadius: 10,
    },
    shadows: Array(25).fill('none') as Theme['shadows'],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: 'inherit',
            color: readCssVariable('--color-text-primary', '#0F172A'),
            backgroundColor: readCssVariable('--color-page-bg', '#F8FAFC'),
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          disableRipple: false,
        },
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-lg)',
            textTransform: 'none',
            fontWeight: 500,
            paddingInline: '0.875rem',
            paddingBlock: '0.5rem',
            boxShadow: 'none',
            transition: 'background-color 150ms ease-out, color 150ms ease-out, border-color 150ms ease-out',
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          },
          sizeSmall: {
            paddingInline: '0.75rem',
            paddingBlock: '0.375rem',
            fontSize: '0.8125rem',
          },
          sizeLarge: {
            paddingInline: '1.125rem',
            paddingBlock: '0.625rem',
            fontSize: '0.9375rem',
          },
          outlined: {
            borderColor: 'var(--color-border)',
            '&:hover': {
              borderColor: 'var(--color-border-strong)',
              backgroundColor: 'var(--color-hover-overlay)',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-lg)',
            color: 'var(--color-text-secondary)',
            transition: 'background-color 150ms ease-out, color 150ms ease-out',
            '&:hover': {
              backgroundColor: 'var(--color-hover-overlay)',
              color: 'var(--color-text-primary)',
            },
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: 'var(--color-surface)',
            backgroundImage: 'none',
            borderRadius: 'var(--radius-xl)',
            color: 'var(--color-text-primary)',
          },
          outlined: {
            borderColor: 'var(--color-border)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--color-surface)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-overlay)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            color: 'var(--color-text-secondary)',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 'var(--radius-lg)',
              backgroundColor: 'var(--color-surface)',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--color-border)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--color-border-strong)',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--color-focus-ring)',
              borderWidth: 2,
            },
            '& .MuiInputLabel-root': {
              color: 'var(--color-text-secondary)',
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--color-surface-raised)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-overlay)',
            marginTop: 4,
          },
          option: {
            fontSize: '0.875rem',
            borderRadius: 'var(--radius-md)',
            marginInline: 4,
            marginBlock: 2,
            paddingInline: 10,
            paddingBlock: 6,
            "&[aria-selected='true']": {
              backgroundColor: 'var(--color-hover-overlay)',
            },
            '&:hover': {
              backgroundColor: 'var(--color-hover-overlay)',
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--color-surface-raised)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-overlay)',
            marginTop: 4,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            borderRadius: 'var(--radius-md)',
            marginInline: 4,
            marginBlock: 2,
            paddingInline: 10,
            paddingBlock: 6,
            '&:hover': {
              backgroundColor: 'var(--color-hover-overlay)',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: 'var(--color-text-primary)',
            color: 'var(--color-text-inverse)',
            fontSize: '0.75rem',
            lineHeight: 1.4,
            borderRadius: 'var(--radius-md)',
            paddingInline: 8,
            paddingBlock: 6,
          },
          arrow: {
            color: 'var(--color-text-primary)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-full)',
            fontWeight: 500,
          },
          outlined: {
            borderColor: 'var(--color-border)',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: 'var(--color-border-subtle)',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-xl)',
            fontSize: '0.875rem',
          },
          standardError: {
            backgroundColor: 'var(--color-sla-breached-bg)',
            color: 'var(--color-sla-breached-fg)',
          },
          standardWarning: {
            backgroundColor: 'var(--color-sla-at-risk-bg)',
            color: 'var(--color-sla-at-risk-fg)',
          },
          standardSuccess: {
            backgroundColor: 'var(--color-sla-healthy-bg)',
            color: 'var(--color-sla-healthy-fg)',
          },
          standardInfo: {
            backgroundColor: 'var(--color-status-new-bg)',
            color: 'var(--color-status-new-fg)',
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--color-surface-raised)',
            color: 'var(--color-text-primary)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-overlay)',
          },
        },
      },
    },
  };
  return createTheme(options);
}

export interface MuiThemeIntegrationProps {
  children: React.ReactNode;
}

export const MuiThemeIntegration: React.FC<MuiThemeIntegrationProps> = ({ children }) => {
  const { resolvedTheme, brandSeed } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const muiTheme = React.useMemo(
    () => {
      void brandSeed;
      return buildMuiTheme(isDark);
    },
    [isDark, brandSeed],
  );

  return <ThemeProvider theme={muiTheme}>{children}</ThemeProvider>;
};

export default MuiThemeIntegration;
